import { useState } from 'react';
import { isApiError } from './api';
import { Alert, Button } from './components';
import type { ErrorOverrides } from './format';

interface Versioned<Draft> {
  draft: Draft;
  /** The version the draft was made from: the one a save sends (optimistic lock). */
  version: number;
  /** The person changed something since the draft was made. */
  dirty: boolean;
}

/**
 * A form's draft together with the version it was made from. Saving sends that version, never
 * the version of whatever the server returned since: a change made elsewhere meanwhile answers
 * 409 CONFLICT instead of being overwritten.
 *
 * While nothing is being edited, the draft follows the server (a refetch, another form on the page
 * that bumped the version). Once the person edits, it stays put and `stale` says the server moved.
 */
export function useVersionedDraft<Source, Draft extends object>(
  source: Source,
  versionOf: (source: Source) => number,
  toDraft: (source: Source) => Draft,
) {
  const [state, setState] = useState<Versioned<Draft>>(() => ({
    draft: toDraft(source),
    version: versionOf(source),
    dirty: false,
  }));
  const live = versionOf(source);
  if (!state.dirty && live !== state.version) {
    // Adjusting state from new props during render (React's documented pattern).
    setState({ draft: toDraft(source), version: live, dirty: false });
  }
  return {
    draft: state.draft,
    baseVersion: state.version,
    stale: state.dirty && live !== state.version,
    /** Merges a change into the draft. */
    set: (change: Partial<Draft>) => {
      setState((current) => ({ ...current, draft: { ...current.draft, ...change }, dirty: true }));
    },
    /** Replaces the draft from a change function. */
    update: (change: (draft: Draft) => Draft) => {
      setState((current) => ({ ...current, draft: change(current.draft), dirty: true }));
    },
    /** Starts again from a server copy (the save's answer, or the latest one). */
    reset: (from: Source) => {
      setState({ draft: toDraft(from), version: versionOf(from), dirty: false });
    },
  };
}

/** The words an app gives for the state of a form that is saved with a version. */
export interface FormWords {
  fixErrors: string;
  saved: string;
  reload: string;
  changedElsewhere: string;
}

/**
 * An app's `StaleNotice`: says that the data changed elsewhere while this form was being edited,
 * with a reload.
 */
export function createStaleNotice(words: Pick<FormWords, 'changedElsewhere' | 'reload'>) {
  return function StaleNotice({ stale, onReload }: { stale: boolean; onReload: () => void }) {
    if (!stale) return null;
    return (
      <div data-testid="stale-notice">
        <Alert tone="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{words.changedElsewhere}</span>
            <Button tone="secondary" onClick={onReload}>
              {words.reload}
            </Button>
          </div>
        </Alert>
      </div>
    );
  };
}

/**
 * An app's `FormStatus`, the outcome of a save: the API's message (with the "changed by someone
 * else" text for a stale version), or the local "fix the fields" note when the browser already
 * found mistakes.
 */
export function createFormStatus(
  errorMessage: (error: unknown, overrides?: ErrorOverrides) => string,
  words: Pick<FormWords, 'fixErrors' | 'saved' | 'reload'>,
) {
  return function FormStatus({
    error,
    localInvalid,
    saved,
    overrides,
    onReload,
  }: {
    error: unknown;
    localInvalid: boolean;
    saved?: boolean;
    overrides?: ErrorOverrides;
    /** Offered when the save hit a stale version (CONFLICT): start again from the server's copy. */
    onReload?: (() => void) | undefined;
  }) {
    if (localInvalid) return <Alert>{words.fixErrors}</Alert>;
    if (error !== null && error !== undefined) {
      const stale =
        onReload !== undefined &&
        isApiError(error, 'CONFLICT') &&
        overrides?.CONFLICT === undefined;
      return (
        <Alert>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{errorMessage(error, overrides)}</span>
            {stale ? (
              <Button tone="secondary" onClick={onReload}>
                {words.reload}
              </Button>
            ) : null}
          </div>
        </Alert>
      );
    }
    if (saved === true) return <Alert tone="success">{words.saved}</Alert>;
    return null;
  };
}
